// EIC API Schema Types - Complete Collection
// Generated from OpenAPI schemas

import { ConnectionTypeEnum, State } from "src/components/Premises/types";

// 1. Main EIC Interface
interface EIC {
  draft_id?: string;
  reCaptcha?: string;
  postcode?: string;
  account_type?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  email?: string;
  phone?: string;
  send_communications_by?: string;
  company_name?: string;
  abn?: string;
  primary_contact_firstName?: string;
  primary_contact_lastName?: string;
  primary_contact_phone?: string;
  primary_contactEmail?: string;
  postal_address_same_as_premise?: boolean;
  fuel_type_for_life_support?: FuelTypeForLifeSupport;
  premise_details?: PostalAddress;
  secondary_account?: SecondaryAccount;
  // New concession block fields
  concession_status?: ConcessionStatus;
  concession_number?: string | null;
  concession_card_type?: string | null;
  consent_electronic_signature?: boolean | null;
  consent_legally_binding?: boolean | null;
}

// concession status flag. null = unset (Yup blocks submit).
type ConcessionStatus = boolean | null;

// 2. Fuel Type for Life Support Enum
enum FuelTypeForLifeSupport {
  ELECTRICITY = "ELECTRICITY",
  GAS = "GAS",
  BOTH = "BOTH",
}

// 4. Property Type Enum
enum PropertyType {
  INDUSTRIAL = "INDUSTRIAL",
  COMMERCIAL = "COMMERCIAL",
  RESIDENTIAL = "RESIDENTIAL",
  FARM = "FARM",
  LIGHTING = "LIGHTING",
  NCONUML = "NCONUML",
}

// 5. Secondary Account Interface
interface SecondaryAccount {
  phone_number: string; // required
  email: string; // required, format: email
  first_name: string; // required
  last_name: string; // required
  date_of_birth: string; // required, format: date
  add_second_name_to_bill: boolean; // required
}

// Project-related types removed as they are not used in EIC components

// 8. Company Interface
interface Company {
  id: string; // required
  msa_number: string; // required
  name: string; // required
  created: string; // required
  updated: string; // required
  operational_team: string; // required
  users: string[]; // required
  contacts: string[]; // required
}

// 9. Attachment Meta Interface and File Upload Type Enum
interface AttachmentMeta {
  id?: string;
  file_upload_type?: FileUploadType;
  file_upload_type_affix?: string;
  file_name?: string;
  link?: string;
}

enum FileUploadType {
  ELECTRICAL_WORKS_REQUEST = "ELECTRICAL_WORKS_REQUEST",
  CERTIFICATE_OF_ELECTRICAL_SAFETY = "CERTIFICATE_OF_ELECTRICAL_SAFETY",
  ADDITIONAL_CERTIFICATE_OF_ELECTRICAL_SAFETY = "ADDITIONAL_CERTIFICATE_OF_ELECTRICAL_SAFETY",
  MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY = "MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY",
  SITE_PLAN = "SITE_PLAN",
  PHOTOS = "PHOTOS",
  QUOTE = "QUOTE",
  PLAN_OF_SUBDIVISION = "PLAN_OF_SUBDIVISION",
  NEW_ENERGY_ACCOUNT_FORM = "NEW_ENERGY_ACCOUNT_FORM",
  HANDOVER_CONSENT_FORM = "HANDOVER_CONSENT_FORM",
  VESI_ABOLISHMENT_FORM = "VESI_ABOLISHMENT_FORM",
  SOLAR_PV_AGREEMENT = "SOLAR_PV_AGREEMENT",
  APPLIANCE_CHECKLIST = "APPLIANCE_CHECKLIST",
  ACCOUNT_MESSAGE_ATTACHMENT = "ACCOUNT_MESSAGE_ATTACHMENT",
  SHIPPER_DOCUMENTS = "SHIPPER_DOCUMENTS",
  OTHER = "OTHER",
}

// 10. Postal Address Interface
interface PostalAddress {
  unit_type?: string;
  unit_number?: string;
  floor_type?: string;
  floor_number?: string;
  building_name?: string;
  location_descriptor?: string;
  street_number?: string;
  street_number_suffix?: string;
  lot_number?: string;
  street_name?: string;
  street_type?: string;
  street_suffix?: string;
  postal_delivery_type?: string;
  postal_delivery_number_prefix?: string;
  postal_delivery_number_value?: string;
  postal_delivery_number_suffix?: string;
  suburb: string; // required
  state: string; // required
  postcode: string; // required
}

// 11. Form Types
type PostalAddressType = "premiseAddress" | "postalAddress" | "poBox";
type BillDeliveryMethod = "post" | "email";

// 12. EIC Form Data Interface
interface EICFormData {
  referenceNumber: string;
  postcode: string;
  // Page 2 fields
  connectionTypes: ConnectionTypeEnum[];
  address: string;
  state: State | "";
  accountType: string;
  firstName: string;
  lastName: string;
  dateOfBirth: Date | null;
  phone: string;
  email: string;
  companyName: string;
  abn: string;
  addSecondaryAccountHolder: boolean;
  secondaryFirstName: string;
  secondaryLastName: string;
  secondaryDateOfBirth: Date | null;
  secondaryPhone: string;
  secondaryEmail: string;
  addSecondaryToBills: boolean;
  // Page 3 fields
  billDeliveryMethod: BillDeliveryMethod;
  postalAddressType: PostalAddressType;
  lifeSupport: string;
  whichEnergy: string;
  // Postal address fields
  postalUnitNo: string;
  postalPremiseType: string;
  postalLevelNo: string;
  postalStreetNo: string;
  postalStreetName: string;
  postalStreetType: string;
  postalSuffix: string;
  postalSuburb: string;
  postalState: string;
  postalPostcode: string;
  // PO Box fields
  poBoxType: string;
  poBoxNo: string;
  poBoxSuburb: string;
  poBoxState: string;
  poBoxPostcode: string;
  // Concession fields
  concessionStatus: ConcessionStatus;
  concessionCardType: string;
  concessionNumber: string;
  consentElectronicSignature: boolean;
  consentLegallyBinding: boolean;
  // Services Australia consent
  consentElectronicSignatureServicesAustralia: boolean;
  consentLegallyBindingServicesAustralia: boolean;
}

export interface NewConnectionFeeScripting {
  /**
   * Fee scripting identifier
   */
  fee_scripting: string;
  /**
   * Price amount as string
   */
  price?: string | null;
  /**
   * Indicates if multiple databases are involved
   */
  is_multi_db?: boolean;
  /**
   * List of LNSP (Licensed Network Service Provider) IDs
   */
  lnsp_ids?: string[];
  /**
   * Indicates if this is single phase connection (electricity only)
   */
  is_single_phase?: boolean;
}

interface NewConnectionFee {
  /**
   * Draft ID for the connection request
   */
  draft_id: string;
  /**
   * Electricity connection fee details
   */
  electricity?: NewConnectionFeeScripting | null;
  /**
   * Gas connection fee details
   */
  gas?: NewConnectionFeeScripting | null;
}

type NewConnectionFeesResponse = NewConnectionFee[];

// Tariff API types
export interface Tariff {
  /**
   * Distributor name
   */
  distributor: string;
  /**
   * Type of tariff
   */
  tariff_type: string;
  /**
   * Estimated annual usage in kWh
   */
  estimated_annual_usage_kwh: string;
  /**
   * Estimated annual cost including GST
   */
  estimated_annual_cost_incl_gst: string;
}

export interface GetTariffsParams {
  /**
   * Account type (required)
   */
  accountType: string;
  /**
   * Array of LNSP (Licensed Network Service Provider) IDs (required)
   */
  lnspIds: string[];
}

export type TariffsResponse = Tariff[];

// Export all types
export {
  EIC,
  FuelTypeForLifeSupport,
  PropertyType,
  SecondaryAccount,
  Company,
  AttachmentMeta,
  FileUploadType,
  PostalAddress,
  EICFormData,
  PostalAddressType,
  BillDeliveryMethod,
  NewConnectionFee,
  NewConnectionFeesResponse,
  ConcessionStatus,
};

// EIC Status enum
export type EICStatus =
  | "EIC_COMM_UNUSED"
  | "EIC_COMM_NOT_SENT"
  | "EIC_COMM_SENT"
  | "EIC_ACCEPTED";

// Postal Address interface
interface PostalAddress {
  unit_type?: string;
  unit_number?: string;
  floor_type?: string;
  floor_number?: string;
  building_name?: string;
  location_descriptor?: string;
  street_number?: string;
  street_number_suffix?: string;
  lot_number?: string;
  street_name?: string;
  street_type?: string;
  street_suffix?: string;
  postal_delivery_type?: string;
  postal_delivery_number_prefix?: string;
  postal_delivery_number_value?: string;
  postal_delivery_number_suffix?: string;
  suburb: string;
  state: string;
  postcode: string;
}

// Secondary Account interface
interface SecondaryAccount {
  phone_number: string;
  email: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  add_second_name_to_bill: boolean;
}

// Account interface
export interface Account {
  id: string;
  locked: boolean;
  first_name: string;
  last_name: string;
  company_name: string;
  company_number: string;
  phone_number: string;
  email: string;
  date_of_birth: string;
  communication_preference: string;
  marketing: boolean;
  is_digital_eic: boolean;
  postal_address_same_as_premise?: boolean;
  secondary_account: SecondaryAccount;
  postal_address: PostalAddress;
}

// EIC Request Meta interface
export interface EicRequestMeta {
  eic_request_id: string;
  eic_status: EICStatus;
  account_details: Account;
  connection_type: ConnectionTypeEnum;
}

// Array type for eic_requests_meta_under_same_premise
export type EicRequestsMetaUnderSamePremise = EicRequestMeta[];
